/**
 * Spoiler obfuscation for hint and reflection text.
 *
 * This is obfuscation, not security, and it should not be mistaken for the
 * latter. The product runs entirely in the learner's tab (docs/browser.md),
 * so missions.json ships with the bundle and so does whatever unhides it;
 * anyone determined can call `revealSpoiler` from the console. The threat
 * this guards against is smaller and real: an honest learner who opens
 * devtools, or views the JSON source out of curiosity, should not have rung
 * 5 of the hint ladder, the full solution, scroll past their eyes. Hidden
 * text looks like noise; the page reveals a rung only when the learner asks
 * for it.
 *
 * The scheme is deliberately modest. Each string is XORed with a keystream
 * from xorshift32, seeded by an FNV-1a hash of the mission id, the rung and
 * a fixed salt, then base64-encoded behind a marker. Different rungs of the
 * same mission get different keystreams, so revealing rung 1 tells you
 * nothing about rung 5. Nothing here is cryptographic and nothing is
 * claimed to be.
 *
 * Everything is hand-rolled because the engine compiles against the bare
 * ES2022 lib (no DOM, no Node): there is no `btoa`, no `TextEncoder` and no
 * crypto API to lean on. The UTF-8 codec mirrors `utf8Length` in posix.ts.
 *
 * `revealSpoiler` leaves unmarked text untouched, so plain missions from the
 * dev server and the author tests flow through the same code path unchanged.
 */
/** Prefix on every hidden string: section sign, scheme name, version. */
export const SPOILER_MARKER = '§eos1:';
/** Part of the seed. Changing it invalidates every built missions.json. */
const SALT = 'engineer-os/spoilers/2026';
/** 32-bit FNV-1a over the UTF-16 code units of `text`. */
function fnv1a(text) {
    let hash = 0x811c9dc5;
    for (let i = 0; i < text.length; i++) {
        hash ^= text.charCodeAt(i);
        // Multiply by the FNV prime 16777619 in 32-bit arithmetic.
        hash = Math.imul(hash, 0x01000193) >>> 0;
    }
    return hash >>> 0;
}
/**
 * The keystream: xorshift32, one byte per step from the low bits. The seed
 * must not be zero (xorshift is stuck there forever), so a zero hash is
 * nudged to a fixed non-zero value.
 */
function keystream(missionId, rung, length) {
    let state = fnv1a(`${missionId}:${rung}:${SALT}`) || 0x9e3779b9;
    const out = new Uint8Array(length);
    for (let i = 0; i < length; i++) {
        state ^= state << 13;
        state >>>= 0;
        state ^= state >>> 17;
        state ^= state << 5;
        state >>>= 0;
        out[i] = state & 0xff;
    }
    return out;
}
/** UTF-8 encode. A lone surrogate becomes U+FFFD, as TextEncoder does. */
function utf8Encode(text) {
    const bytes = [];
    for (let i = 0; i < text.length; i++) {
        let code = text.charCodeAt(i);
        if (code >= 0xd800 && code <= 0xdbff && (text.charCodeAt(i + 1) & 0xfc00) === 0xdc00) {
            code = 0x10000 + ((code - 0xd800) << 10) + (text.charCodeAt(i + 1) - 0xdc00);
            i++;
        }
        else if (code >= 0xd800 && code <= 0xdfff) {
            code = 0xfffd;
        }
        if (code < 0x80)
            bytes.push(code);
        else if (code < 0x800)
            bytes.push(0xc0 | (code >> 6), 0x80 | (code & 0x3f));
        else if (code < 0x10000)
            bytes.push(0xe0 | (code >> 12), 0x80 | ((code >> 6) & 0x3f), 0x80 | (code & 0x3f));
        else
            bytes.push(0xf0 | (code >> 18), 0x80 | ((code >> 12) & 0x3f), 0x80 | ((code >> 6) & 0x3f), 0x80 | (code & 0x3f));
    }
    return Uint8Array.from(bytes);
}
/** UTF-8 decode. Malformed sequences become U+FFFD rather than throwing. */
function utf8Decode(bytes) {
    let out = '';
    for (let i = 0; i < bytes.length;) {
        const b0 = bytes[i] ?? 0;
        let code;
        let need;
        if (b0 < 0x80) {
            code = b0;
            need = 0;
        }
        else if ((b0 & 0xe0) === 0xc0) {
            code = b0 & 0x1f;
            need = 1;
        }
        else if ((b0 & 0xf0) === 0xe0) {
            code = b0 & 0x0f;
            need = 2;
        }
        else if ((b0 & 0xf8) === 0xf0) {
            code = b0 & 0x07;
            need = 3;
        }
        else {
            out += '�';
            i++;
            continue;
        }
        // A lead byte whose continuation bytes run past the end is malformed.
        let ok = i + need < bytes.length;
        for (let k = 1; k <= need; k++) {
            const b = bytes[i + k] ?? 0;
            if ((b & 0xc0) !== 0x80) {
                ok = false;
                break;
            }
            code = (code << 6) | (b & 0x3f);
        }
        if (!ok) {
            out += '�';
            i++;
            continue;
        }
        i += need + 1;
        out += code < 0x10000 ? String.fromCharCode(code) : String.fromCodePoint(code);
    }
    return out;
}
const B64 = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+/';
function base64Encode(bytes) {
    let out = '';
    for (let i = 0; i < bytes.length; i += 3) {
        const b0 = bytes[i] ?? 0;
        const b1 = bytes[i + 1] ?? 0;
        const b2 = bytes[i + 2] ?? 0;
        const triple = (b0 << 16) | (b1 << 8) | b2;
        out += B64.charAt((triple >> 18) & 63) + B64.charAt((triple >> 12) & 63);
        out += i + 1 < bytes.length ? B64.charAt((triple >> 6) & 63) : '=';
        out += i + 2 < bytes.length ? B64.charAt(triple & 63) : '=';
    }
    return out;
}
/** Returns null on any character outside the alphabet, so a mangled string is left as it is. */
function base64Decode(text) {
    const clean = text.replace(/=+$/, '');
    const bytes = [];
    let acc = 0;
    let bits = 0;
    for (let i = 0; i < clean.length; i++) {
        const v = B64.indexOf(clean.charAt(i));
        if (v < 0)
            return null;
        acc = (acc << 6) | v;
        bits += 6;
        if (bits >= 8) {
            bits -= 8;
            bytes.push((acc >> bits) & 0xff);
        }
    }
    return Uint8Array.from(bytes);
}
function xor(bytes, key) {
    const out = new Uint8Array(bytes.length);
    for (let i = 0; i < bytes.length; i++)
        out[i] = (bytes[i] ?? 0) ^ (key[i] ?? 0);
    return out;
}
/**
 * Hide `text` for the given mission and rung. Hints use their own rung
 * number (1..5); the reflection uses rung 0. Hiding already-hidden text
 * would double-wrap it, so hidden input is returned as is.
 */
export function hideSpoiler(text, missionId, rung) {
    if (text.startsWith(SPOILER_MARKER))
        return text;
    const bytes = utf8Encode(text);
    return SPOILER_MARKER + base64Encode(xor(bytes, keystream(missionId, rung, bytes.length)));
}
/**
 * Inverse of `hideSpoiler`. Text without the marker is returned unchanged,
 * which is what keeps plain (unbuilt) missions working everywhere.
 */
export function revealSpoiler(text, missionId, rung) {
    if (!text.startsWith(SPOILER_MARKER))
        return text;
    const bytes = base64Decode(text.slice(SPOILER_MARKER.length));
    if (bytes === null)
        return text;
    return utf8Decode(xor(bytes, keystream(missionId, rung, bytes.length)));
}
//# sourceMappingURL=spoilers.js.map